import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ReportLayout from '@/pages/reports/components/ReportLayout';
import ReportTable from '@/pages/reports/components/ReportTable';
import SummaryCards from '@/pages/reports/components/SummaryCards';
import { filterItems } from '@/api/item.api';
import { useWarehouseStore } from '@/stores/warehouseStore';
import axios from 'axios';

export const getStockLedgerApi = async (params) => {
  const token = localStorage.getItem('token');

  const res = await axios.get(
    `${import.meta.env.VITE_API_URL}/api/v1/reports/stock-ledger`,
    {
      params,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return res.data;
};

export const getBatchMovementsApi = async (batchNumber: string) => {
  const token = localStorage.getItem('token');
  const res = await axios.get(
    `${import.meta.env.VITE_API_URL}/api/v1/inventory/batches/${batchNumber}/movements`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    },
  );
  return res.data;
};

function formatINR(n: any) {
  const num = Number(n || 0);
  return `₹${num.toLocaleString('en-IN')}`;
}

function getFirstDayOfMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
}

export default function StockLedgerReport() {
  const navigate = useNavigate();
  const today = new Date().toISOString().split('T')[0];
  const [itemId, setItemId] = useState('');
  const { selectedWarehouseId } = useWarehouseStore();
  const warehouseId =
    selectedWarehouseId && selectedWarehouseId !== 'ALL'
      ? selectedWarehouseId
      : '';
  const [from, setFrom] = useState(getFirstDayOfMonth());
  const [to, setTo] = useState(today);
  const [viewMode, setViewMode] = useState<'date' | 'batch' | 'serial'>('date');
  const [generated, setGenerated] = useState(false);
  const [items, setItems] = useState([]);
  const [ledger, setLedger] = useState<any[]>([]);
  const [batchLedger, setBatchLedger] = useState<any[]>([]);
  const [serialLedger, setSerialLedger] = useState<any[]>([]);
  const [openingBalance, setOpeningBalance] = useState(0);
  const [isFetching, setIsFetching] = useState(false);

  // Selected batch for movements modal
  const [selectedBatch, setSelectedBatch] = useState<any | null>(null);
  const [batchMovements, setBatchMovements] = useState<any[]>([]);
  const [loadingMovements, setLoadingMovements] = useState(false);

  useEffect(() => {
    const loadItems = async () => {
      try {
        const warehouseIdToUse =
          selectedWarehouseId && selectedWarehouseId !== 'ALL'
            ? selectedWarehouseId
            : '';
        if (!warehouseIdToUse) {
          setItems([]);
          setItemId('');
          return;
        }
        const res = await filterItems({ warehouseId: warehouseIdToUse });
        setItems(Array.isArray(res?.data) ? res.data : []);
        setItemId((prev) => {
          const exists = (res?.data || []).some((item) => item.id === prev);
          return exists ? prev : '';
        });
      } catch (err) {
        console.error('Failed to load items', err);
        setItems([]);
        setItemId('');
      }
    };
    loadItems();
  }, [selectedWarehouseId]);

  const canGenerate = !!itemId && !!warehouseId;

  const fetchStockLedger = async (mode = viewMode) => {
    if (!canGenerate) return;

    try {
      setIsFetching(true);

      const res = await getStockLedgerApi({
        itemId,
        warehouseId,
        fromDate: from,
        toDate: to,
        viewBy: mode,
      });

      if (mode === 'serial') {
        setSerialLedger(res?.data || []);
      } else if (mode === 'batch') {
        const batchData = (res?.data || []).map((b: any) => ({
          ...b,
          inQty: Number(b.in_qty || 0),
          outQty: Number(b.out_qty || 0),
          remainingQty: Number(b.remaining_qty || 0),
        }));
        setBatchLedger(batchData);
      } else {
        const normalized = (res?.data || []).map((r: any) => ({
          ...r,
          inQty: Number(r.in_qty || 0),
          outQty: Number(r.out_qty || 0),
          balance: Number(r.balance || 0),
          rate: Number(r.rate || 0),
          value: Number(r.value || 0),
        }));
        setLedger(normalized);
        setOpeningBalance(res?.openingBalance || 0);
      }
    } catch (err) {
      console.error('Stock ledger fetch failed:', err);
      setLedger([]);
      setBatchLedger([]);
      setSerialLedger([]);
      setOpeningBalance(0);
    } finally {
      setIsFetching(false);
    }
  };

  const handleGenerate = () => {
    if (!canGenerate) return;
    setGenerated(true);
    fetchStockLedger(viewMode);
  };

  const handleModeChange = (newMode: 'date' | 'batch' | 'serial') => {
    setViewMode(newMode);
    if (generated && canGenerate) {
      fetchStockLedger(newMode);
    }
  };

  const handleViewBatchMovements = async (batch: any) => {
    setSelectedBatch(batch);
    setLoadingMovements(true);
    try {
      const res = await getBatchMovementsApi(batch.batch_number);
      setBatchMovements(res?.data || []);
    } catch (err) {
      console.error('Failed to load batch movements:', err);
      setBatchMovements([]);
    } finally {
      setLoadingMovements(false);
    }
  };

  const closingBalance =
    ledger.length > 0 ? ledger[ledger.length - 1].balance || 0 : openingBalance;

  const totalIn = ledger.reduce((s, r) => s + Number(r.inQty || 0), 0);
  const totalOut = ledger.reduce((s, r) => s + Number(r.outQty || 0), 0);

  const totalBatchIn = batchLedger.reduce((s, b) => s + Number(b.inQty || 0), 0);
  const totalBatchOut = batchLedger.reduce((s, b) => s + Number(b.outQty || 0), 0);
  const totalBatchRemaining = batchLedger.reduce((s, b) => s + Number(b.remainingQty || 0), 0);

  const totalSerials = serialLedger.length;
  const serialsInStock = serialLedger.filter((s) => s.status === 'IN_STOCK').length;
  const serialsSold = serialLedger.filter((s) => s.status === 'SOLD').length;
  const serialsOther = serialLedger.filter((s) => s.status !== 'IN_STOCK' && s.status !== 'SOLD').length;

  const exportColumns = viewMode === 'serial' ? [
    { header: 'Serial Number', key: 'serial_number', width: 22 },
    { header: 'Status', key: 'status', width: 14 },
    { header: 'Condition', key: 'condition', width: 14 },
    { header: 'Warehouse', key: 'warehouse_name', width: 18 },
    { header: 'Source', key: 'source_no', width: 18 },
    { header: 'Warranty Exp', key: 'warranty_expiry_date', width: 14 },
    { header: 'Customer', key: 'customer_name', width: 18 },
    { header: 'Created Date', key: 'date', width: 14 },
  ] : viewMode === 'batch' ? [
    { header: 'Batch No', key: 'batch_number', width: 18 },
    { header: 'Source', key: 'source_no', width: 16 },
    { header: 'In Qty', key: 'inQty', width: 10 },
    { header: 'Out Qty', key: 'outQty', width: 10 },
    { header: 'Remaining', key: 'remainingQty', width: 10 },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'Mfg Date', key: 'mfg_date', width: 12 },
    { header: 'Expiry Date', key: 'expiry_date', width: 12 },
  ] : [
    { header: 'Date', key: 'date', width: 14 },
    { header: 'Voucher Type', key: 'voucherType', width: 16 },
    { header: 'Voucher No', key: 'voucherNo', width: 18 },
    { header: 'Party', key: 'party', width: 22 },
    { header: 'In Qty', key: 'inQty', width: 10 },
    { header: 'Out Qty', key: 'outQty', width: 10 },
    { header: 'Balance', key: 'balance', width: 10 },
    { header: 'Rate', key: 'rate', width: 12 },
    { header: 'Value', key: 'value', width: 16 },
  ];

  const selectedItem = items.find((i: any) => i.id === itemId);

  const filterBar = (
    <div className="flex flex-wrap items-end gap-3">
      {/* View Mode Toggle */}
      <div>
        <label className="block text-xs font-medium text-[#64748b] mb-1">
          View Mode
        </label>
        <div className="flex items-center gap-1 bg-slate-100 p-0.5 rounded-lg border border-slate-200">
          <button
            type="button"
            onClick={() => handleModeChange('date')}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors cursor-pointer ${
              viewMode === 'date'
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <i className="ri-calendar-line mr-1" />
            By Date
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('batch')}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors cursor-pointer ${
              viewMode === 'batch'
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <i className="ri-archive-line mr-1" />
            By Batch
          </button>
          <button
            type="button"
            onClick={() => handleModeChange('serial')}
            className={`px-3 py-1 text-xs font-semibold rounded-md transition-colors cursor-pointer ${
              viewMode === 'serial'
                ? 'bg-white text-indigo-700 shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            <i className="ri-barcode-line mr-1" />
            By Serial
          </button>
        </div>
      </div>

      <div>
        <label className="block text-xs font-medium text-[#64748b] mb-1">
          Item <span className="text-red-500">*</span>
        </label>
        <select
          value={itemId}
          onChange={(e) => setItemId(e.target.value)}
          className="h-8 px-2 border border-[#e2e8f0] rounded-lg text-sm bg-white text-[#1e293b] cursor-pointer focus:outline-none focus:border-[#4f46e5] min-w-[200px]"
        >
          <option value="">-- Select Item --</option>
          {items.map((item) => (
            <option key={item.id} value={item.id}>
              {item.code} — {item.name}
            </option>
          ))}
        </select>
      </div>

      <div>
        <label className="block text-xs font-medium text-[#64748b] mb-1">
          From Date
        </label>
        <input
          type="date"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          className="h-8 px-2 border border-[#e2e8f0] rounded-lg text-sm bg-white text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
        />
      </div>
      <div>
        <label className="block text-xs font-medium text-[#64748b] mb-1">
          To Date
        </label>
        <input
          type="date"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          className="h-8 px-2 border border-[#e2e8f0] rounded-lg text-sm bg-white text-[#1e293b] focus:outline-none focus:border-[#4f46e5]"
        />
      </div>
    </div>
  );

  const VOUCHER_COLORS: Record<string, string> = {
    Opening: 'bg-slate-100 text-slate-600',
    Purchase: 'bg-green-100 text-green-700',
    Sale: 'bg-blue-100 text-blue-700',
    'Sale Return': 'bg-indigo-100 text-indigo-700',
    'Purchase Return': 'bg-orange-100 text-orange-700',
    'Transfer In': 'bg-teal-100 text-teal-700',
    'Transfer Out': 'bg-purple-100 text-purple-700',
    'Adj-In': 'bg-cyan-100 text-cyan-700',
    'Adj-Out': 'bg-rose-100 text-rose-700',
  };

  return (
    <ReportLayout
      title="Stock Ledger"
      subtitle={
        selectedItem
          ? `${selectedItem.name} (${selectedItem.code}) · Mode: ${viewMode === 'serial' ? 'By Serial' : viewMode === 'batch' ? 'By Batch' : 'By Date'}`
          : 'Select item and warehouse to view movement history'
      }
      filterBar={filterBar}
      onGenerate={handleGenerate}
      generating={isFetching}
      generated={generated && canGenerate}
      exportData={(viewMode === 'serial' ? serialLedger : viewMode === 'batch' ? batchLedger : ledger) as unknown as Record<string, unknown>[]}
      exportColumns={exportColumns}
      exportFilename={`stock-ledger-${viewMode}-${itemId}`}
    >
      {generated && canGenerate && (
        <>
          {viewMode === 'date' ? (
            <>
              <SummaryCards
                cards={[
                  {
                    label: 'Opening Balance',
                    value: `${openingBalance} ${selectedItem?.unitName ?? ''}`,
                    icon: 'ri-archive-line',
                    color: 'bg-slate-100 text-slate-600',
                  },
                  {
                    label: 'Total Inward',
                    value: `+${totalIn}`,
                    icon: 'ri-arrow-down-circle-line',
                    color: 'bg-green-50 text-green-600',
                  },
                  {
                    label: 'Total Outward',
                    value: `-${totalOut}`,
                    icon: 'ri-arrow-up-circle-line',
                    color: 'bg-red-50 text-red-500',
                  },
                  {
                    label: 'Closing Balance',
                    value: `${closingBalance} ${selectedItem?.unitName ?? ''}`,
                    icon: 'ri-stack-line',
                    color: 'bg-indigo-50 text-[#4f46e5]',
                  },
                ]}
              />
              <ReportTable
                keyField="id"
                data={ledger}
                loading={isFetching}
                columns={[
                  {
                    header: 'Date',
                    accessor: 'date',
                    className: 'text-xs text-[#64748b]',
                  },
                  {
                    header: 'Voucher Type',
                    accessor: (r) => {
                      const cls =
                        VOUCHER_COLORS[r.voucherType] ??
                        'bg-slate-100 text-slate-600';
                      return (
                        <span
                          className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${cls}`}
                        >
                          {r.voucherType}
                        </span>
                      );
                    },
                  },
                  {
                    header: 'Voucher No',
                    accessor: 'voucherNo',
                    className: 'font-mono text-xs',
                  },
                  {
                    header: 'Source',
                    accessor: (r) => (
                      <span className="text-xs text-[#64748b]">
                        {(r as unknown as { source?: string }).source ?? r.party}
                      </span>
                    ),
                  },
                  {
                    header: 'In Qty',
                    accessor: (r) =>
                      r.inQty > 0 ? (
                        <span className="text-green-600 font-semibold">
                          +{r.inQty}
                        </span>
                      ) : (
                        <span className="text-[#94a3b8]">—</span>
                      ),
                    headerClassName: 'text-right',
                    className: 'text-right',
                  },
                  {
                    header: 'Out Qty',
                    accessor: (r) =>
                      r.outQty > 0 ? (
                        <span className="text-red-500 font-semibold">
                          -{r.outQty}
                        </span>
                      ) : (
                        <span className="text-[#94a3b8]">—</span>
                      ),
                    headerClassName: 'text-right',
                    className: 'text-right',
                  },
                  {
                    header: 'Balance',
                    accessor: (r) => (
                      <span className="font-bold text-[#1e293b]">{r.balance}</span>
                    ),
                    headerClassName: 'text-right',
                    className: 'text-right',
                  },
                  {
                    header: 'Rate',
                    accessor: (r) => formatINR(r.rate),
                    headerClassName: 'text-right',
                    className: 'text-right text-[#64748b]',
                  },
                  {
                    header: 'Value',
                    accessor: (r) => (
                      <span className="font-medium">{formatINR(r.value)}</span>
                    ),
                    headerClassName: 'text-right',
                    className: 'text-right',
                  },
                ]}
                footerRow={
                  <>
                    <td colSpan={4} className="px-3 py-2.5 text-xs text-[#64748b]">
                      Closing Balance
                    </td>
                    <td className="px-3 py-2.5 text-right text-green-600">
                      +{totalIn}
                    </td>
                    <td className="px-3 py-2.5 text-right text-red-500">
                      -{totalOut}
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold text-[#1e293b]">
                      {closingBalance}
                    </td>
                    <td colSpan={2} />
                  </>
                }
              />
            </>
          ) : viewMode === 'batch' ? (
            /* ── BY BATCH VIEW ── */
            <>
              <SummaryCards
                cards={[
                  {
                    label: 'Total Batches',
                    value: `${batchLedger.length}`,
                    icon: 'ri-archive-line',
                    color: 'bg-indigo-50 text-indigo-600',
                  },
                  {
                    label: 'Total Batch In',
                    value: `+${totalBatchIn}`,
                    icon: 'ri-arrow-down-circle-line',
                    color: 'bg-emerald-50 text-emerald-600',
                  },
                  {
                    label: 'Total Batch Out',
                    value: `-${totalBatchOut}`,
                    icon: 'ri-arrow-up-circle-line',
                    color: 'bg-rose-50 text-rose-600',
                  },
                  {
                    label: 'Batch Stock Remaining',
                    value: `${totalBatchRemaining} ${selectedItem?.unitName ?? ''}`,
                    icon: 'ri-stack-line',
                    color: 'bg-violet-50 text-violet-600',
                  },
                ]}
              />

              <ReportTable
                keyField="id"
                data={batchLedger}
                loading={isFetching}
                columns={[
                  {
                    header: 'Batch Number',
                    accessor: (b) => (
                      <div className="flex items-center gap-1.5 font-mono text-xs font-semibold text-indigo-700">
                        <i className="ri-barcode-line text-indigo-500" />
                        <span>{b.batch_number}</span>
                      </div>
                    ),
                  },
                  {
                    header: 'Source',
                    accessor: (b) => (
                      <div className="text-xs">
                        <span className="font-semibold text-slate-700">{b.source_type}</span>
                        <span className="text-slate-400 mx-1">·</span>
                        <span className="text-slate-500 font-mono">{b.source_no}</span>
                      </div>
                    ),
                  },
                  {
                    header: 'Mfg / Exp Date',
                    accessor: (b) => (
                      <div className="text-xs text-slate-500">
                        <div>Mfg: {b.mfg_date || '—'}</div>
                        <div className="text-[11px] text-slate-400">Exp: {b.expiry_date || 'None'}</div>
                      </div>
                    ),
                  },
                  {
                    header: 'In Qty',
                    accessor: (b) => (
                      <span className="text-emerald-600 font-semibold">
                        +{b.inQty}
                      </span>
                    ),
                    headerClassName: 'text-right',
                    className: 'text-right',
                  },
                  {
                    header: 'Out Qty',
                    accessor: (b) => (
                      <span className="text-rose-600 font-semibold">
                        -{b.outQty}
                      </span>
                    ),
                    headerClassName: 'text-right',
                    className: 'text-right',
                  },
                  {
                    header: 'Remaining',
                    accessor: (b) => (
                      <span className="font-bold text-slate-800">
                        {b.remainingQty}
                      </span>
                    ),
                    headerClassName: 'text-right',
                    className: 'text-right',
                  },
                  {
                    header: 'Status',
                    accessor: (b) => {
                      const st = b.status || 'ACTIVE';
                      const badgeClass =
                        st === 'ACTIVE'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : st === 'PARTIAL'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-slate-100 text-slate-600 border-slate-200';
                      return (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${badgeClass}`}>
                          {st}
                        </span>
                      );
                    },
                  },
                  {
                    header: 'Action',
                    accessor: (b) => (
                      <button
                        type="button"
                        onClick={() => handleViewBatchMovements(b)}
                        className="px-2.5 py-1 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1"
                      >
                        <i className="ri-history-line" />
                        Movements
                      </button>
                    ),
                    className: 'text-right',
                    headerClassName: 'text-right',
                  },
                ]}
                footerRow={
                  <>
                    <td colSpan={3} className="px-3 py-2.5 text-xs text-slate-500 font-semibold">
                      Total ({batchLedger.length} Batches)
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold text-emerald-600">
                      +{totalBatchIn}
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold text-rose-600">
                      -{totalBatchOut}
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold text-slate-900">
                      {totalBatchRemaining}
                    </td>
                    <td colSpan={2} />
                  </>
                }
              />
            </>
          ) : (
            /* ── BY SERIAL VIEW ── */
            <>
              <SummaryCards
                cards={[
                  {
                    label: 'Total Serials Recorded',
                    value: `${totalSerials}`,
                    icon: 'ri-barcode-line',
                    color: 'bg-indigo-50 text-indigo-700',
                  },
                  {
                    label: 'In Stock (Available)',
                    value: `${serialsInStock}`,
                    icon: 'ri-checkbox-circle-line',
                    color: 'bg-emerald-50 text-emerald-600',
                  },
                  {
                    label: 'Units Sold',
                    value: `${serialsSold}`,
                    icon: 'ri-shopping-cart-line',
                    color: 'bg-blue-50 text-blue-600',
                  },
                  {
                    label: 'Other / Non-Available',
                    value: `${serialsOther}`,
                    icon: 'ri-alert-line',
                    color: 'bg-amber-50 text-amber-600',
                  },
                ]}
              />

              <ReportTable
                keyField="id"
                data={serialLedger}
                loading={isFetching}
                columns={[
                  {
                    header: 'Serial Number',
                    accessor: (s) => (
                      <div className="flex items-center gap-1.5 font-mono text-xs font-bold text-slate-800">
                        <i className="ri-barcode-box-line text-blue-600 text-sm" />
                        <span>{s.serial_number}</span>
                      </div>
                    ),
                  },
                  {
                    header: 'Status',
                    accessor: (s) => {
                      const st = s.status || 'IN_STOCK';
                      const badgeClass =
                        st === 'IN_STOCK'
                          ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                          : st === 'SOLD'
                          ? 'bg-blue-50 text-blue-700 border-blue-200'
                          : st === 'RESERVED'
                          ? 'bg-indigo-50 text-indigo-700 border-indigo-200'
                          : st === 'IN_REPAIR'
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : 'bg-rose-50 text-rose-700 border-rose-200';
                      return (
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${badgeClass}`}>
                          {st}
                        </span>
                      );
                    },
                  },
                  {
                    header: 'Condition',
                    accessor: (s) => (
                      <span className="text-xs text-slate-600 font-medium">
                        {s.condition || 'GOOD'}
                      </span>
                    ),
                  },
                  {
                    header: 'Warehouse',
                    accessor: (s) => (
                      <span className="text-xs text-slate-600 font-medium">
                        {s.warehouse_name || '—'}
                      </span>
                    ),
                  },
                  {
                    header: 'Source / GRN',
                    accessor: (s) => (
                      <span className="text-xs font-mono text-slate-600">
                        {s.source_no || 'Opening'}
                      </span>
                    ),
                  },
                  {
                    header: 'Warranty Expiry',
                    accessor: (s) => (
                      <span className="text-xs text-slate-600">
                        {s.warranty_expiry_date || 'None'}
                      </span>
                    ),
                  },
                  {
                    header: 'Customer / Party',
                    accessor: (s) => (
                      <span className="text-xs text-slate-600">
                        {s.customer_name || '—'}
                      </span>
                    ),
                  },
                  {
                    header: 'Action',
                    accessor: (s) => (
                      <button
                        type="button"
                        onClick={() => navigate(`/inventory/serials/${s.id}`)}
                        className="px-2.5 py-1 text-xs font-semibold text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors cursor-pointer inline-flex items-center gap-1"
                      >
                        <i className="ri-history-line" />
                        Timeline
                      </button>
                    ),
                    className: 'text-right',
                    headerClassName: 'text-right',
                  },
                ]}
                footerRow={
                  <>
                    <td colSpan={2} className="px-3 py-2.5 text-xs text-slate-500 font-semibold">
                      Total ({serialLedger.length} Serials)
                    </td>
                    <td colSpan={2} className="px-3 py-2.5 text-xs text-emerald-700 font-semibold">
                      In Stock: {serialsInStock}
                    </td>
                    <td colSpan={2} className="px-3 py-2.5 text-xs text-blue-700 font-semibold">
                      Sold: {serialsSold}
                    </td>
                    <td colSpan={2} className="px-3 py-2.5 text-xs text-amber-700 font-semibold text-right">
                      Other: {serialsOther}
                    </td>
                  </>
                }
              />
            </>
          )}
        </>
      )}

      {(!generated || !canGenerate) && (
        <div className="bg-white border border-[#e2e8f0] rounded-xl flex items-center justify-center py-24">
          <div className="text-center text-[#64748b]">
            <i className="ri-file-list-3-line text-5xl text-slate-200 block mb-3" />
            <p className="text-sm font-medium">
              Select an item and warehouse to view its stock movement history
            </p>
          </div>
        </div>
      )}

      {/* ── BATCH MOVEMENTS MODAL ── */}
      {selectedBatch && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl overflow-hidden flex flex-col max-h-[85vh]">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold">
                  <i className="ri-history-line text-lg" />
                </div>
                <div>
                  <h3 className="text-base font-bold text-slate-800">
                    Batch Movements: {selectedBatch.batch_number}
                  </h3>
                  <p className="text-xs text-slate-500">
                    {selectedBatch.item_name} · Warehouse: {selectedBatch.warehouse_name} · Status: {selectedBatch.status}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setSelectedBatch(null)}
                className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 cursor-pointer"
              >
                <i className="ri-close-line text-lg" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto">
              <div className="grid grid-cols-3 gap-3 mb-5">
                <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg">
                  <span className="text-[11px] font-medium text-emerald-700 uppercase">Initial Inward</span>
                  <div className="text-lg font-bold text-emerald-800 mt-0.5">
                    {selectedBatch.inQty} {selectedBatch.unit || 'units'}
                  </div>
                </div>
                <div className="p-3 bg-rose-50 border border-rose-100 rounded-lg">
                  <span className="text-[11px] font-medium text-rose-700 uppercase">Total Consumed</span>
                  <div className="text-lg font-bold text-rose-800 mt-0.5">
                    {selectedBatch.outQty} {selectedBatch.unit || 'units'}
                  </div>
                </div>
                <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-lg">
                  <span className="text-[11px] font-medium text-indigo-700 uppercase">Remaining Stock</span>
                  <div className="text-lg font-bold text-indigo-800 mt-0.5">
                    {selectedBatch.remainingQty} {selectedBatch.unit || 'units'}
                  </div>
                </div>
              </div>

              {loadingMovements ? (
                <div className="py-12 text-center text-slate-400">
                  <i className="ri-loader-4-line animate-spin text-2xl mb-2" />
                  <p className="text-xs">Loading batch transactions...</p>
                </div>
              ) : batchMovements.length === 0 ? (
                <div className="py-8 text-center text-slate-400 text-xs">
                  No movement records found for this batch.
                </div>
              ) : (
                <div className="border border-slate-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold">
                      <tr>
                        <th className="px-3 py-2 text-left">Date & Time</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Reference</th>
                        <th className="px-3 py-2 text-left">Party</th>
                        <th className="px-3 py-2 text-right">Quantity</th>
                        <th className="px-3 py-2 text-right">Balance After</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {batchMovements.map((m) => (
                        <tr key={m.id} className="hover:bg-slate-50/50">
                          <td className="px-3 py-2 text-slate-600">{m.date_time || String(m.created_at).slice(0, 16)}</td>
                          <td className="px-3 py-2">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              m.movement_type === 'IN'
                                ? 'bg-emerald-100 text-emerald-800'
                                : 'bg-rose-100 text-rose-800'
                            }`}>
                              {m.movement_type}
                            </span>
                          </td>
                          <td className="px-3 py-2 font-mono text-slate-700">{m.reference_type}</td>
                          <td className="px-3 py-2 text-slate-600">{m.party_name || '—'}</td>
                          <td className={`px-3 py-2 text-right font-semibold ${
                            m.movement_type === 'IN' ? 'text-emerald-600' : 'text-rose-600'
                          }`}>
                            {m.movement_type === 'IN' ? `+${m.qty}` : `-${m.qty}`}
                          </td>
                          <td className="px-3 py-2 text-right font-bold text-slate-800">{m.balance_after}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="px-6 py-3 border-t border-slate-100 bg-slate-50 flex justify-end">
              <button
                type="button"
                onClick={() => setSelectedBatch(null)}
                className="px-4 py-1.5 text-xs font-medium text-slate-600 hover:text-slate-800 hover:bg-slate-200/50 rounded-lg cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </ReportLayout>
  );
}
